/**
 * "Keys, tokens & IDs" — Reference section (ADR-0074).
 * A consolidated lookup of the credential and identifier formats used across
 * Syncanix, reconciled from api-key.graphql (gak_ secret / pk_ publishable) and
 * the MCP connection token. The authoritative how-to lives on auth/api-keys and
 * mcp/authentication; this page is the at-a-glance table.
 */
import type { DocPageContent } from '../types';

const KEY_FORMATS = `gak_<env>_<32 base32 chars>    # secret key — CLI / MCP / server-side only
pk_<test|live>_<32 base32 chars>  # publishable key — embedded in your page`;

export const REFERENCE_KEY_FORMATS_PAGE: DocPageContent = {
  slug: 'reference/key-formats',
  titleKey: 'docs.reference.keys.title',
  descriptionKey: 'docs.reference.keys.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.reference.keys.lead' },

    { kind: 'heading', level: 2, id: 'api-keys', textKey: 'docs.reference.keys.api.heading' },
    {
      kind: 'definitions',
      items: [
        { termKey: 'docs.reference.keys.secret.term', descKey: 'docs.reference.keys.secret.desc' },
        {
          termKey: 'docs.reference.keys.publishable.term',
          descKey: 'docs.reference.keys.publishable.desc',
        },
      ],
    },
    { kind: 'code', language: 'text', code: KEY_FORMATS },
    {
      kind: 'callout',
      tone: 'caution',
      titleKey: 'docs.reference.keys.once.title',
      textKey: 'docs.reference.keys.once.body',
      links: {
        keys: { target: { docSlug: 'auth/api-keys' }, labelKey: 'docs.reference.keys.once.link' },
      },
    },

    { kind: 'heading', level: 2, id: 'mcp', textKey: 'docs.reference.keys.mcp.heading' },
    { kind: 'paragraph', textKey: 'docs.reference.keys.mcp.body' },

    { kind: 'heading', level: 2, id: 'environments', textKey: 'docs.reference.keys.envs.heading' },
    { kind: 'paragraph', textKey: 'docs.reference.keys.envs.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.reference.keys.env.dev.term',
          descKey: 'docs.reference.keys.env.dev.desc',
        },
        {
          termKey: 'docs.reference.keys.env.prod.term',
          descKey: 'docs.reference.keys.env.prod.desc',
        },
      ],
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.reference.keys.next.keys.title',
          textKey: 'docs.reference.keys.next.keys.text',
          to: { docSlug: 'auth/api-keys' },
        },
        {
          titleKey: 'docs.reference.keys.next.mcp.title',
          textKey: 'docs.reference.keys.next.mcp.text',
          to: { docSlug: 'mcp/authentication' },
        },
      ],
    },
  ],
};
